import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { card } from "@/components/atoms";
import { Logo } from "@/components/icons";
import { enterDemo } from "@/lib/pocketbase";
import { DEFAULT_THEME, loadTheme } from "@/lib/theme";

export function Login() {
  const { loginWithPassword, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // The sign-in screen is always the house colour, whatever this browser last
  // chose — it is the front door, not your workspace. Set on <html> directly
  // rather than through applyTheme, which would also overwrite the stored
  // choice and the app-icon cookie. On the way out, restore what is stored NOW,
  // not what was showing at mount: signing in adopts the account's colour
  // before this screen unmounts, and the stale one would paint over it.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", DEFAULT_THEME);
    return () => { root.setAttribute("data-theme", loadTheme().theme); };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await loginWithPassword(email, password);
    } catch {
      setError("Sign-in failed. Check your email and password.");
    }
  }

  return (
    // 100dvh, not 100vh: iOS measures 100vh against the *expanded* viewport, so
    // the card sits under the Safari toolbar until it happens to retract. The
    // card was a fixed 380px, which overflows a 375pt phone sideways.
    <div style={{
      display: "flex", minHeight: "100dvh", alignItems: "center", justifyContent: "center",
      background: "var(--bg)", padding: "24px 16px calc(24px + var(--safe-b))",
    }}>
      <div style={{ ...card, width: "min(380px, 100%)", padding: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
          <Logo />
          <div>
            <div style={{ fontSize: 15.5, fontWeight: 500, letterSpacing: "-0.01em" }}>Project Hub</div>
            <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Vandehey Strategies</div>
          </div>
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
            style={inputStyle} autoComplete="username"
          />
          <input
            type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            style={inputStyle} autoComplete="current-password"
          />
          {error && <div style={{ fontSize: 12.5, color: "var(--warn)" }}>{error}</div>}
          <button type="submit" disabled={loading} style={primaryBtn}>
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div style={{ textAlign: "center", margin: "14px 0", fontSize: 12, color: "var(--muted)" }}>or</div>

        <a href="/auth/google/start" style={{ ...primaryBtn, display: "block", textAlign: "center", background: "var(--surface)", color: "var(--ink2)", border: "1px solid var(--line2)" }}>
          Continue with Google
        </a>

        {/* No account needed: the demo runs entirely in this browser against
            sample data, so it can be handed to anyone without touching the
            server or exposing real work. */}
        <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--tint2)", textAlign: "center" }}>
          <button
            onClick={enterDemo}
            style={{
              ...primaryBtn, width: "100%", background: "var(--accentTint)", color: "var(--accent)",
              border: "1px solid var(--accentLine)",
            }}
          >
            Explore the demo
          </button>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 8, lineHeight: 1.5 }}>
            Sample projects, in this browser only. Nothing is sent to the server, and
            you can leave any time.
          </div>
        </div>
      </div>
    </div>
  );
}

// Phones get 16px from the global rule in index.css — under that iOS Safari
// zooms the page in on focus and never zooms back out.
const inputStyle: React.CSSProperties = {
  padding: "10px 12px", borderRadius: 8, border: "1px solid var(--line2)",
  background: "var(--surface)", fontSize: 13.5,
};

const primaryBtn: React.CSSProperties = {
  padding: "10px 14px", borderRadius: 999, border: "none", background: "var(--accent)",
  color: "var(--onAccent)", fontSize: 13.5, fontWeight: 500, cursor: "pointer", textDecoration: "none",
};
